/**
 * Redirect-based SSO with the issuer.
 *
 * Ported from ICRM's `frontend/src/lib/auth-redirect.ts` — the established shape in the
 * estate, and the only one that has actually met auth.xpertte.com:
 *
 *   1. no token, or a token past `exp`  →  navigate to
 *        {AUTH_URL}/login?client_id=…&redirect_uri={origin}/auth/callback&return_to=…
 *   2. the issuer authenticates, signs RS256, and redirects back to the callback
 *   3. the callback keeps the token and replaces the URL with `return_to`
 *   4. sign-out is {AUTH_URL}/logout?client_id=…&return_to={origin}
 *
 * Three differences from ICRM's version, each for a reason:
 *
 *   * **The token is read from the fragment as well as the query.** ICRM reads only
 *     `?token=`. A fragment is never sent to a server and never appears in a `Referer`,
 *     so it is the better of the two; supporting both means the issuer may use either
 *     without this shell needing a change. The callback clears whichever it used.
 *   * **`return_to` is validated on the way OUT as well as on the way back.** ICRM
 *     validates the value it receives; a value that leaves as `//evil.com` and comes back
 *     unchanged would be validated correctly and still have handed an external origin to
 *     the issuer to reflect.
 *   * **`audience` is sent only when configured.** The audience `inai-inventory` is not
 *     yet registered with the issuer (D-12), and an issuer that rejects unknown query
 *     parameters would fail the whole login rather than the audience check.
 */

import { SHELL_CONFIG } from '../config';

export const CALLBACK_PATH = '/auth/callback';

/**
 * A `return_to` that cannot leave this origin.
 *
 * Rejects protocol-relative (`//evil.com`), absolute (`https://evil.com`) and anything
 * that is not a rooted path. This is the whole open-redirect defence and it is
 * deliberately a whitelist of one shape rather than a blacklist of known-bad ones.
 */
export function safeReturnTo(value: string | null | undefined, fallback = '/items'): string {
  if (!value) return fallback;
  if (value.startsWith('//')) return fallback;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return fallback;
  return value.startsWith('/') ? value : fallback;
}

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function callbackUrl(): string {
  const base = SHELL_CONFIG.basePath === '/' ? '' : SHELL_CONFIG.basePath;
  return `${window.location.origin}${base}${CALLBACK_PATH}`;
}

export function buildLoginUrl(returnTo?: string): string {
  const params = new URLSearchParams({
    client_id: SHELL_CONFIG.clientId,
    redirect_uri: callbackUrl(),
    return_to: safeReturnTo(returnTo ?? currentPath()),
  });
  if (SHELL_CONFIG.audience) params.set('audience', SHELL_CONFIG.audience);
  return `${SHELL_CONFIG.authUrl.replace(/\/$/, '')}/login?${params.toString()}`;
}

export function buildLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: SHELL_CONFIG.clientId,
    return_to: window.location.origin + (SHELL_CONFIG.basePath === '/' ? '' : SHELL_CONFIG.basePath),
  });
  return `${SHELL_CONFIG.authUrl.replace(/\/$/, '')}/logout?${params.toString()}`;
}

export interface CallbackResult {
  token: string | null;
  returnTo: string;
  /** The issuer can also come back with `error=access_denied` and no token. */
  error: string | null;
}

/**
 * Read the token out of wherever the issuer put it.
 *
 * The fragment is preferred: it never reaches a server, so a token that arrives there has
 * not been written into an access log on the way. The query string is accepted because
 * that is what ICRM's callback documents today, and this shell must work against the
 * issuer as it is rather than as it should be.
 */
export function readCallback(href: string = window.location.href): CallbackResult {
  const url = new URL(href);
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
  const query = url.searchParams;

  const token =
    fragment.get('access_token') ??
    fragment.get('token') ??
    query.get('access_token') ??
    query.get('token');

  const error = fragment.get('error') ?? query.get('error');
  const returnTo = safeReturnTo(fragment.get('return_to') ?? query.get('return_to'));

  return { token: token && token.trim().length > 0 ? token.trim() : null, returnTo, error };
}

/** Leave. Separated so the redirect can be asserted, and stubbed, without navigating. */
export function navigateTo(url: string): void {
  window.location.assign(url);
}
